<template>
  <v-container fluid>
    <v-data-table-server
      :headers="headers"
      :items="contracts"
      :search="search"
      density="compact"
      :items-length="totalItems"
      :loading="loadingDatatables"
      @update:sortBy="toSort"
    >
      <template v-slot:top>
        <v-toolbar flat>
          <v-autocomplete
            v-model="selectedCustomerNumber"
            :items="uniqueCustomerNumbers"
            density="compact"
            :label="$t('admin.select-customer')"
            class="pl-2 mr-2"
            :prepend-inner-icon="mdiAccountFilter"
            variant="solo-filled"
            flat
            hide-details
            clearable
            @update:modelValue="filterByCustomerNumber"
            style="max-width: 250px"
          ></v-autocomplete>
          <v-text-field
            v-model="search"
            density="compact"
            :label="this.$t('search')"
            class="pl-2"
            :prepend-inner-icon="mdiMagnify"
            @update:modelValue="toSearchBack"
            variant="solo-filled"
            flat
            hide-details
            single-line
          ></v-text-field>
          <v-spacer></v-spacer>
          <v-btn
            variant="elevated"
            class="mb-2 text-white mr-3"
            :prepend-icon="mdiFileDocumentPlus"
            color="#ffb300"
            @click="openDialog"
            dark
          >
            {{ $t("admin.new-contract") }}
          </v-btn>
        </v-toolbar>
      </template>
      <template v-slot:item.startDate="{ item }">
        {{ formatDate(item.startDate) }}
      </template>
      <template v-slot:item.endDate="{ item }">
        {{ formatDate(item.endDate) }}
      </template>
      <template v-slot:item.customerNumbers="{ item }">
        <v-chip-group v-model="item.customerNumbers" disabled>
          <v-chip
            v-for="customer in item.customerNumbers"
            :key="customer"
            :text="customer"
            size="small"
          ></v-chip>
        </v-chip-group>
      </template>
      <template v-slot:item.active="{ item }">
        <v-chip
          :color="item.active ? 'green' : 'grey'"
          :text="
            item.active ? $t('active-contracts') : $t('inactive-contracts')
          "
          size="small"
        ></v-chip>
      </template>
      <template v-slot:item.actions="{ item }">
        <v-icon class="me-2" size="small" @click="editItem(item)">
          {{ mdiPencil }}
        </v-icon>
        <v-icon class="me-2" size="small" @click="deleteItem(item)">
          {{ mdiDelete }}
        </v-icon>
        <v-tooltip location="top" text="Voir les statistiques du contrat">
          <template v-slot:activator="{ props }">
            <v-icon
              v-bind="props"
              class="me-2"
              size="small"
              color="#ffb300"
              @click="showContractStats(item)"
            >
              {{ mdiChartBox }}
            </v-icon>
          </template>
        </v-tooltip>
        <v-tooltip location="top" text="Recalculer les points historiques">
          <template v-slot:activator="{ props }">
            <v-icon
              v-bind="props"
              class="me-2"
              size="small"
              color="blue"
              @click="openRecalculateDialog(item)"
            >
              {{ mdiHistory }}
            </v-icon>
          </template>
        </v-tooltip>
      </template>

      <template v-slot:bottom>
        <pagination-dynamic :store="'contracts'"></pagination-dynamic>
      </template>
    </v-data-table-server>
    <add-edit-contract
      :modelDialog="contractDialog"
      @close="closeContractDialog"
    ></add-edit-contract>
    <show-stats-customer-admin
      :modelDialog="statsContractDialog"
      :context="'contract'"
      :contractInfo="selectedContract"
      @close="closeStatsContractDialog"
    ></show-stats-customer-admin>
    <recalculate-historical-dialog
      :modelDialog="recalculateDialog"
      :contractInfo="selectedContract"
      @close="closeRecalculateDialog"
      @confirm="recalculateHistoricalPoints"
    ></recalculate-historical-dialog>
  </v-container>
</template>

<script>
import {
  mdiPencil,
  mdiDelete,
  mdiMagnify,
  mdiFileDocumentPlus,
  mdiAccountFilter,
  mdiChartBox,
  mdiHistory,
} from "@mdi/js";
import PaginationDynamic from "@/components/PaginationDynamic.vue";
import AddEditContract from "./admin/AddEditContract.vue";
import ShowStatsCustomerAdmin from "./admin/ShowStatsCustomerAdmin.vue";
import RecalculateHistoricalDialog from "./admin/RecalculateHistoricalDialog.vue";
import paginate from "@/mixins/paginations";
import { mapState } from "vuex";

export default {
  name: "ContractAdmin",
  mixins: [paginate],
  components: {
    AddEditContract,
    PaginationDynamic,
    ShowStatsCustomerAdmin,
    RecalculateHistoricalDialog,
  },
  data() {
    return {
      mdiFileDocumentPlus,
      mdiMagnify,
      mdiPencil,
      mdiDelete,
      mdiAccountFilter,
      mdiChartBox,
      mdiHistory,
      dialog: false,
      dialogDelete: false,
      contractDialog: false,
      statsContractDialog: false,
      recalculateDialog: false,
      selectedContract: null,
      itemsPerPage: 15,
      page: 1,
      selectedCustomerNumber: null,
      headers: [
        { title: this.$t("name"), key: "name" },
        { title: this.$t("start-date"), key: "startDate" },
        { title: this.$t("end-date"), key: "endDate" },
        { title: this.$t("active-contracts"), key: "active" },
        { title: this.$t("admin.customers"), key: "customerNumbers" },
        {
          title: this.$t("actions"),
          key: "actions",
          sortable: false,
          align: "end",
        },
      ],
    };
  },
  computed: {
    ...mapState({
      contracts: (state) => state.contracts.items,
      loadingDatatables: (state) => state.loadingTables,
      totalItems: (state) => state.paginations.totalCount,
      editedId: (state) => state.contracts.editedId,
      allCustomerNumbers: (state) => state.contracts.allCustomerNumbers,
    }),
    pageCount() {
      return Math.ceil(this.contracts.length / this.itemsPerPage);
    },
    search: {
      get() {
        return this.$store.state.paginations.search;
      },
      set(value) {
        this.$store.commit("paginations/setSearch", value);
      },
    },
    /**
     * Retourne la liste unique de tous les customerNumbers triée
     */
    uniqueCustomerNumbers() {
      return [...this.allCustomerNumbers].sort();
    },
  },
  methods: {
    /**
     * Filtre les contrats par customerNumber sélectionné en déclenchant une recherche back
     */
    filterByCustomerNumber(customerNumber) {
      if (customerNumber) {
        this.search = customerNumber;
        this.toSearchBack(customerNumber, 100);
      } else {
        // Si on efface la sélection, on vide aussi la recherche
        this.search = "";
        this.toSearchBack("", 100);
      }
    },
    closeContractDialog() {
      this.contractDialog = false;
      this.$store.commit("contracts/setEditedId", -1);
      this.$store.commit("files/setItems", []);
      this.$store.commit(
        "contracts/setEditedItem",
        Object.assign({}, this.$store.state.contracts.defaultItem),
      );
      this.$store.commit("contracts/setEditedItemExclusions", []);
    },
    formatDate(date) {
      return new Date(date).toLocaleDateString();
    },
    toSort(item) {
      this.sortBy(item, "contracts");
    },
    toSearchBack(value, time = 500) {
      this.searchBack(value, time, "contracts");
    },
    openDialog() {
      this.$store
        .dispatch("fetchContractAdmin")
        .then(() => {
          this.$store.commit("contracts/setEditedId", -1);
          this.$store.commit(
            "contracts/setEditedItem",
            Object.assign({}, this.$store.state.contracts.defaultItem),
          );
          this.contractDialog = true;
        })
        .catch(() => {
          this.$store.dispatch("showSnackbar", {
            message: this.$t("alert.error.contract-edit"),
            color: "error",
          });
        });
    },
    editItem(item) {
      this.$store
        .dispatch("contracts/getExclusionsByContractId", item.id)
        .then(() => {
          if (item.customerNumbers && item.customerNumbers.length > 0) {
            const data = {
              customerNumbers: item.customerNumbers,
              contractId: item.id,
            };
            this.$store
              .dispatch("fetchContractAdminWithCustomerNumbers", data)
              .then(() => {
                this.$store.commit("contracts/setEditedId", item.id);
                this.$store.commit(
                  "contracts/setEditedItem",
                  Object.assign({}, item),
                );
                this.contractDialog = true;
              })
              .catch(() => {
                this.$store.dispatch("showSnackbar", {
                  message: this.$t("alert.error.contract-edit"),
                  color: "error",
                });
              });
          } else {
            this.$store
              .dispatch("fetchContractAdmin")
              .then(() => {
                this.$store.commit("contracts/setEditedId", item.id);
                this.$store.commit(
                  "contracts/setEditedItem",
                  Object.assign({}, item),
                );
                this.contractDialog = true;
              })
              .catch(() => {
                this.$store.dispatch("showSnackbar", {
                  message: this.$t("alert.error.contract-edit"),
                  color: "error",
                });
              });
          }
        })
        .catch(() => {
          this.$store.dispatch("showSnackbar", {
            message: this.$t("alert.error.contract-edit"),
            color: "error",
          });
        });
    },
    deleteItem(item) {
      if (confirm(this.$t("alert.delete.contract"))) {
        this.$store
          .dispatch("contracts/deleteItem", item.id)
          .then(() => {
            this.$store.dispatch("showSnackbar", {
              message: this.$t("alert.success.contract-delete"),
              color: "success",
            });
            this.toSearchBack(this.search, 500, "contracts");
          })
          .catch(() => {
            this.$store.dispatch("showSnackbar", {
              message: this.$t("alert.error.contract-delete"),
              color: "error",
            });
          });
      }
    },
    /**
     * Affiche les statistiques d'un contrat spécifique
     */
    showContractStats(item) {
      this.$store.commit("setOverlay", true);
      this.selectedContract = item;
      this.$store
        .dispatch("statsCustomer/fetchStatsByContractId", item.id)
        .then((response) => {
          this.$store.commit("setOverlay", false);
          if (response.data !== "") {
            this.statsContractDialog = true;
          } else {
            this.$store.dispatch("showSnackbar", {
              message:
                this.$t("alert.warning.no-stats-contract") ||
                "Aucune statistique disponible pour ce contrat",
              color: "warning",
            });
          }
        })
        .catch(() => {
          this.$store.commit("setOverlay", false);
          this.$store.dispatch("showSnackbar", {
            message:
              this.$t("alert.error.contract-stats-failed") ||
              "Erreur lors de la récupération des statistiques du contrat",
            color: "error",
          });
        });
    },
    /**
     * Ferme la dialog des statistiques du contrat
     */
    closeStatsContractDialog() {
      this.statsContractDialog = false;
      this.selectedContract = null;
    },
    /**
     * Ouvre la dialog de recalcul historique pour un contrat
     */
    openRecalculateDialog(item) {
      this.selectedContract = item;
      this.recalculateDialog = true;
    },
    /**
     * Ferme la dialog de recalcul historique
     */
    closeRecalculateDialog() {
      this.recalculateDialog = false;
      this.selectedContract = null;
    },
    /**
     * Lance le recalcul historique des points pour un contrat
     */
    recalculateHistoricalPoints({ contractId, targetDate }) {
      this.$store.commit("setOverlay", true);
      this.$store
        .dispatch("statsCustomer/recalculateHistoricalPoints", {
          contractId,
          targetDate,
        })
        .then(() => {
          this.$store.commit("setOverlay", false);
          this.closeRecalculateDialog();
          this.$store.dispatch("showSnackbar", {
            message:
              this.$t("alert.success.historical-recalculation") ||
              "Recalcul historique effectué avec succès",
            color: "success",
          });
          // Optionnel : rafraîchir les données si nécessaire
          this.toSearchBack(this.search, 100);
        })
        .catch((error) => {
          this.$store.commit("setOverlay", false);
          const errorMessage =
            error.response?.status === 404
              ? this.$t("alert.error.contract-not-found") ||
                "Contrat introuvable"
              : this.$t("alert.error.historical-recalculation") ||
                "Erreur lors du recalcul historique";
          this.$store.dispatch("showSnackbar", {
            message: errorMessage,
            color: "error",
          });
        });
    },
  },
  mounted() {
    // Charge la liste complète des customerNumbers au montage du composant
    this.$store.dispatch("contracts/fetchAllCustomerNumbers").catch(() => {
      this.$store.dispatch("showSnackbar", {
        message: this.$t("alert.error.loading-customer-numbers"),
        color: "error",
      });
    });
  },
};
</script>
